// Standard library imports
import React, { useEffect, useState, useRef } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { get as lodashGet, set as lodashSet } from 'lodash';

// Internal module imports
import { useLoader, useUserRole } from '../../../../../hooks';
import Typography from '../../../../../components/Typography/Typography';
import { Input, ToggleButtonWithState } from '../../../../../components/Inputs/Input';
import { momentTimeFormater } from '../../../../../utils/timeHelper';
import bidClasses from '../../Bidding.module.css';
import allDeviceClasses from '../../../../../styles/AllDevices.module.css';
import { getTotalHoursForDay } from '../../../../../utils/dateHelper';
import { SecondaryBundleBidValidator } from '../../../../../validations/BiddingService/Bidding';
import { RootBundleBiddingService } from '../../../../../services/BiddingService/Root/BundleBiddingService';
import { AdminBundleBiddingService } from '../../../../../services/BiddingService/Admin/BundleBiddingService';
import ModalComponent from '../../../../../components/ModalComponent/ModalComponent';
import { OutOfMarket } from '../../ModalComponent/OutOfMarket';

const getInitialValues = (date, bidStatus = [], instances = [], capacity = {}, price = [], secondaryBidToggle = false, alreadyBidCreatedHours = []) => {
    const totalHours = getTotalHoursForDay(momentTimeFormater(date).format('YYYY-MM-DD'));
    let temp = {
        capacity: {},
        price: [],
    };

    Array.from({ length: totalHours }, (__, idx) => {
        instances.map((ins) => {
            if ((bidStatus.length && lodashGet(bidStatus, [idx, 'capacity'], 0) === 0) || secondaryBidToggle)
                lodashSet(temp, ['capacity', ins.instanceId, idx], alreadyBidCreatedHours.includes(idx) ? 0 : lodashGet(capacity, [ins.instanceId, idx], 0));
            else lodashSet(temp, ['capacity', ins.instanceId, idx], null);
        });

        if ((bidStatus.length && lodashGet(bidStatus, [idx, 'capacity'], 0) === 0) || secondaryBidToggle)
            temp.price.push(alreadyBidCreatedHours.includes(idx) ? 0 : lodashGet(price, [idx], 0));
        else lodashSet(temp, ['price', idx], null);
    });

    return temp;
};

const getBidStatus = (bids = []) => {
    const temp = {};
    if (bids.length) {
        bids.map((b) => {
            lodashSet(temp, b.instanceId, lodashGet(b, ['primary', 'privider', 'bidStatus'], []));
        });
    }

    return temp;
};

export const CreateSecondaryBundleBid = ({ data, closeModal, getBids }) => {
    const formikRef = useRef();
    const [startLoader, stopLoader] = useLoader();
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBundleBiddingService : AdminBundleBiddingService;
    const { instanceBundle, bundleBid } = data;
    const pairs = instanceBundle.pairs || [];
    const bidStatus = lodashGet(bundleBid, ['primary', 'provider', 'bidStatus']);
    const indivisualBidStatus = getBidStatus(bundleBid.bids);
    const [alreadyBidCreatedBids, setAlreadyBidCreatedBids] = useState([]);
    const [initialValues, setInitialValues] = useState({
        capacity: [],
        price: [],
    });
    const [secondaryBidToggle, setIsSecondaryBidToggle] = useState(false);
    const [approvedCapacity, setApprovedCapacity] = useState(0);

    const [outOfMarketHours, setOutOfMarketHours] = useState([]);

    const [showAllHours, setShowAllHours] = useState(false);

    const [outOfMarketModal, setOutOfMarketModal] = useState({
        status: false,
    });
    const [lastBidData, setLastBidData] = useState({
        capacity: {},
        price: [],
    });

    useEffect(() => {
        if (secondaryBidToggle) {
            const capAndPrice = getInitialValues(bundleBid.date, bidStatus, pairs, lastBidData.capacity, lastBidData.price, secondaryBidToggle, alreadyBidCreatedBids);
            setInitialValues(capAndPrice);
        }
    }, [secondaryBidToggle]);

    useEffect(() => {
        const capAndPrice = getInitialValues(
            bundleBid.date,
            showAllHours ? [] : bidStatus,
            pairs,
            lastBidData.capacity,
            lastBidData.price,
            secondaryBidToggle,
            alreadyBidCreatedBids
        );
        setInitialValues(capAndPrice);
    }, [showAllHours]);

    useEffect(() => {
        if (bundleBid.instanceBundleId) {
            getLastBid();
            getOtherMarketsBids();
            getApprovedCapacity();
        }
    }, [bundleBid.instanceBundleId]);

    const getLastBid = () => {
        service.GetLastBid(instanceBundle.value, { type: 'secondary' }, startLoader, handleGetLastBidSuccess, handleError, stopLoader);
    };

    const handleGetLastBidSuccess = ({ data }) => {
        const bundleBid = lodashGet(data, ['data', 'bundleBid']);
        if (bundleBid) {
            const capacity = {};
            lodashGet(data, ['data', 'bids'], []).map((bid) => {
                lodashSet(capacity, [bid.instanceId], lodashGet(bid, ['secondary', 'capacity'], []));
            });
            const price = lodashGet(bundleBid, ['secondary', 'price'], []);
            const capAndPrice = getInitialValues(bundleBid.date, bidStatus, pairs, capacity, price, secondaryBidToggle, alreadyBidCreatedBids);
            setLastBidData({ capacity, price });
            setInitialValues(capAndPrice);
        } else {
            const capAndPrice = getInitialValues(bundleBid.date, bidStatus, pairs, {}, [], secondaryBidToggle, alreadyBidCreatedBids);
            setLastBidData({ capacity: {}, price: [] });
            setInitialValues(capAndPrice);
        }
    };

    const getOtherMarketsBids = () => {
        service.GetOtherMarketsBid(
            instanceBundle.value,
            { date: momentTimeFormater(bundleBid.date).format('YYYY-MM-DD'), type: 'secondary' },
            startLoader,
            handleOtherMarketsBidSuccess,
            handleError,
            stopLoader
        );
    };

    const handleOtherMarketsBidSuccess = ({ data }) => {
        setAlreadyBidCreatedBids(lodashGet(data, ['data'], []));
    };

    const getApprovedCapacity = () => {
        service.GetApprovedCapacity(instanceBundle.value, startLoader, handleApprovedCapacitySuccess, handleError, stopLoader);
    };

    const handleApprovedCapacitySuccess = ({ data }) => {
        setApprovedCapacity(data.data || {});
    };

    const handleSubmit = (val) => {
        val.date = bundleBid.date;
        if (bundleBid._id) service.CreateSecondaryBid(bundleBid._id, val, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ res }) => {
        toast.success('Bid Created Successfully');
        getBids();
        closeModal();
    };

    useEffect(() => {
        if (outOfMarketHours.length) {
            const tempCap = {};
            const tempPrice = lodashGet(initialValues, ['price'], []).map(() => 0);
            const capacity = lodashGet(initialValues, ['capacity'], {});
            for (let key in capacity) {
                const insCap = [];
                lodashGet(capacity, key, []).map((c, index) => {
                    if (outOfMarketHours.includes(`HOUR__${index}`)) insCap.push(-c);
                    else insCap.push(0);
                });
                lodashSet(tempCap, key, insCap);
            }
            setInitialValues({ capacity: tempCap, price: tempPrice });
        } else setInitialValues(lastBidData);
    }, [outOfMarketHours]);

    const handleError = (err) => toast.error(lodashGet(err, ['response', 'data', 'message']));

    return (
        <div>
            <div className={bidClasses.TitleWrapper}>
                <Typography content={`${bundleBid.market} Secondary Bid ( ${momentTimeFormater(bundleBid.date).format('YYYY-MM-DD')} )`} size="16" />
                {initialValues.price?.filter((e) => e != null).length > 0 && (
                    <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                        <div onClick={() => setOutOfMarketModal({ status: true })}>
                            <button type="button" className="btn-primary">
                                Out of Market
                            </button>
                        </div>
                        {!secondaryBidToggle && <ToggleButtonWithState value={showAllHours} onChange={(e) => setShowAllHours(e.target.checked)} label="All Hours" />}
                    </div>
                )}
            </div>
            <div>
                {initialValues.price?.filter((e) => e != null).length > 0 || secondaryBidToggle ? (
                    <div>
                        <div className={bidClasses.FormWrapper}>
                            <Formik
                                enableReinitialize
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                                innerRef={formikRef}
                                validationSchema={SecondaryBundleBidValidator(indivisualBidStatus, approvedCapacity, pairs)}
                            >
                                {({ values }) => {
                                    return (
                                        <Form>
                                            {[
                                                {
                                                    label: 'Capacity (Mw)',
                                                    value: 'capacity',
                                                },
                                                {
                                                    label: 'Price (€)',
                                                    value: 'price',
                                                },
                                            ].map((type) => (
                                                <div>
                                                    {type.value === 'capacity' ? (
                                                        <div>
                                                            {pairs?.map((el, i) => (
                                                                <div key={i}>
                                                                    <div>
                                                                        <div className={bidClasses.Subtitle}>
                                                                            Capacity(Mw) - {el.instanceName}{' '}
                                                                            {alreadyBidCreatedBids.includes(el.instanceId) && (
                                                                                <span className="required">Bid is already created.</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className={bidClasses.CreateBidForm}>
                                                                        {lodashGet(values, ['capacity', el.instanceId], []).map(
                                                                            (val, index) =>
                                                                                val !== null && (
                                                                                    <div>
                                                                                        <div className={allDeviceClasses.FieldControl}>
                                                                                            <label htmlFor={`${type.value}.${index}`}>
                                                                                                Slot {index + 1} {' / Hour '}
                                                                                                {momentTimeFormater(bundleBid.date)
                                                                                                    .startOf('day')
                                                                                                    .clone()
                                                                                                    .add(index, 'hour')
                                                                                                    .hour()}
                                                                                                -
                                                                                                {momentTimeFormater(bundleBid.date)
                                                                                                    .startOf('day')
                                                                                                    .clone()
                                                                                                    .add(index + 1, 'hour')
                                                                                                    .hour()}
                                                                                                <span className="required">*</span>
                                                                                            </label>

                                                                                            <Input
                                                                                                name={`capacity.${el.instanceId}.${index}`}
                                                                                                id={`capacity.${el.instanceId}.${index}`}
                                                                                                type={'number'}
                                                                                                style={{ marginTop: '0' }}
                                                                                                step="0.1" // increasing or decreasing number by 0.1
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className={bidClasses.Subtitle}>{type.label}</div>
                                                            <div className={bidClasses.CreateBidForm}>
                                                                {lodashGet(values, [type.value]).map(
                                                                    (val, index) =>
                                                                        val !== null && (
                                                                            <div>
                                                                                <div className={allDeviceClasses.FieldControl}>
                                                                                    <label htmlFor={`${type.value}.${index}`}>
                                                                                        Slot {index + 1} {' / Hour '}
                                                                                        {momentTimeFormater(bundleBid.date).startOf('day').clone().add(index, 'hour').hour()}-
                                                                                        {momentTimeFormater(bundleBid.date)
                                                                                            .startOf('day')
                                                                                            .clone()
                                                                                            .add(index + 1, 'hour')
                                                                                            .hour()}
                                                                                        {type.value === 'price' ? ' Price' : ' '}
                                                                                        <span className="required">*</span>
                                                                                    </label>
                                                                                    <Input
                                                                                        name={`${type.value}.${index}`}
                                                                                        id={`${type.value}.${index}`}
                                                                                        type={'number'}
                                                                                        style={{ marginTop: '0' }}
                                                                                        step="0.1" // increasing or decreasing number by 0.1
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </div>
                        <div className={allDeviceClasses.ButtonContainer}>
                            <div>
                                <button type="button" className="btn-secondary" onClick={closeModal}>
                                    Cancel
                                </button>
                            </div>
                            <div>
                                <button type="submit" className="btn-primary" onClick={() => formikRef.current.submitForm()}>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ textAlign: 'center' }}>
                            <span>Looks like result for primary bid are not announced yet.</span>
                            <div>OR</div>
                            <span>Result were in favour for all hours.</span>
                            <div>OR</div>
                            <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                                <ToggleButtonWithState value={secondaryBidToggle} onChange={(e) => setIsSecondaryBidToggle(e.target.checked)} label="Primary bid not available" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {outOfMarketModal.status && (
                <ModalComponent
                    isOpen={outOfMarketModal.status}
                    setOpen={() => {
                        setOutOfMarketModal({ status: false });
                    }}
                >
                    <OutOfMarket bid={bundleBid} outOfMarketHours={outOfMarketHours} setOutOfMarketHours={setOutOfMarketHours} setOpen={setOutOfMarketModal} />
                </ModalComponent>
            )}
        </div>
    );
};
